var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useState } from 'react';
import { FaSteam } from 'react-icons/fa';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Checkbox } from 'root/src/client/v2/common/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from 'root/src/client/v2/common/components/ui/tabs';
import { getErrorMessage } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { RedesignGiveawaySmsVerify } from 'root/src/client/v2/_pages/creator/redesign/RedesignGiveawaySmsVerify';
import { RedesignGiveawayTermsModal } from 'root/src/client/v2/_pages/creator/redesign/RedesignGiveawayTermsModal';
import { useCreatorGiveawayEntry } from 'root/src/client/v2/_pages/creator/redesign/useCreatorGiveawayEntry';
// Redesign rewrite of the legacy `VerificationModal` (the "Oops, one more step!" Steam / SMS verification
// step + terms checkbox, legacy was Redux/`useBetween` coupled). Steam and SMS run through
// `useCreatorGiveawayEntry` (v2 fetcher, same gateway endpoints). On success the active giveaway is
// refetched and the modal closes; the caller's CTA then reflects the entered state.
export const RedesignGiveawayVerificationModal = memo(({ giveaway, creatorUserId, termsRequired }) => {
    const { show, hide } = useRedesignModal();
    const [tab, setTab] = useState('steam');
    const [termsAccepted, setTermsAccepted] = useState(!termsRequired);
    const { enterViaSteam, enterViaSms, isSteamLoading } = useCreatorGiveawayEntry({ giveaway, creatorUserId });
    const termsGateMessage = 'Please agree to the Terms and Conditions to proceed.';
    const isGated = termsRequired && !termsAccepted;
    const onSteamClick = (event) => __awaiter(void 0, void 0, void 0, function* () {
        if (isGated) {
            toast.error(termsGateMessage);
            return;
        }
        try {
            yield enterViaSteam(event);
            toast.success('Successfully entered the giveaway.');
            hide();
        }
        catch (error) {
            if (error instanceof Error && error.message === 'Steam sign-in popup closed') {
                return;
            }
            toast.error(getErrorMessage(error));
        }
    });
    const onSmsVerified = () => __awaiter(void 0, void 0, void 0, function* () {
        yield enterViaSms();
        toast.success('Successfully entered the giveaway.');
        hide();
    });
    return (<RedesignModalShell className="sm:max-w-lg">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-cyan/30 bg-neon-cyan/10">
					<VerifiedUserOutlinedIcon sx={{ fontSize: 24 }} className="text-neon-cyan"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">One more step</h2>
				<p className="max-w-sm text-sm text-muted-foreground">
					We use verification to prevent giveaway fraud and ensure fair participation. This is a one-time process.
				</p>
			</div>

			<Tabs value={tab} onValueChange={value => setTab(value)} className="flex w-full flex-col items-center gap-4">
				<TabsList>
					<TabsTrigger value="steam"><FaSteam /> Steam</TabsTrigger>
					<TabsTrigger value="sms">SMS</TabsTrigger>
				</TabsList>

				<div className="w-full">
					{tab === 'steam' ? (<div className="flex flex-col items-center gap-2">
							<Button type="button" variant="brand" loading={isSteamLoading} disabled={isGated} onClick={onSteamClick} className="w-full rounded-xl text-sm font-semibold">
								<FaSteam /> Sign in with Steam
							</Button>
							{!giveaway.isSteamEntered && (<span className="text-xs font-medium text-neon-cyan">Sign in with Steam to earn +2 extra entries.</span>)}
						</div>) : (<RedesignGiveawaySmsVerify onVerified={onSmsVerified} disabled={isGated} disabledMessage={termsGateMessage}/>)}
				</div>
			</Tabs>

			{termsRequired && (<label className="flex w-full cursor-pointer items-center justify-center gap-2 text-sm text-muted-foreground">
					<Checkbox checked={termsAccepted} onCheckedChange={setTermsAccepted} aria-label="Agree to the terms and conditions"/>
					<span>
						I agree to the{' '}
						<button type="button" onClick={() => show(<RedesignGiveawayTermsModal />, { replace: false })} className="font-medium text-neon-cyan underline-offset-2 hover:underline">
							Terms and Conditions
						</button>
					</span>
				</label>)}
		</RedesignModalShell>);
});
